import { useRef, useState } from 'react';
import { MaybeAsync } from 'zod/v4/core/util';

/**
 * Hook pour mettre en attente une action sync/async
 *
 * - `prepareAction` pour l'enregistrer
 * - `performAction` pour l'exécuter
 * - `clearAction` pour l'annuler
 * - `actionExists` pour vérifier si une action est actuellement enregistrée
 * - `isPerforming` pour vérifier si une action est actuellement en exécution
 */
export function useDeferredAction() {
  const deferredAsyncActionRef = useRef<(() => MaybeAsync<unknown>) | null>(null);
  const [isPerforming, setIsPerforming] = useState<boolean>(false);

  const prepareAction = (cb: () => MaybeAsync<unknown>) => deferredAsyncActionRef.current = cb;

  const clearAction = () => deferredAsyncActionRef.current = null;

  const performAction = async () => {
    try {
      setIsPerforming(true);
      await deferredAsyncActionRef.current?.();
      setIsPerforming(false);
      clearAction();
    } catch (err: unknown) {
      setIsPerforming(false);
      clearAction();
      throw err;
    }
  };

  const actionExists = !!deferredAsyncActionRef.current;

  return { prepareAction, clearAction, performAction, actionExists, isPerforming };
}